import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import { createSelector } from '@reduxjs/toolkit';
import { ShippingMethodsGroups } from '@yme/common/redux/cart/constants';
import { getCart, getCartServiceCodes, getCartUnitServiceCodes, getHasGYTRProduct, getIsAllProductsApparel, getIsLeasingEnabled, getIsOnlyGYTRProduct, } from '@yme/common/redux/cart/selectors';
import { PORTUGAL_COUNTRY_CODE } from '@yme/common/selectors';
import { ADYEN, CYCLESCHEME, PAYEVER, SANTANDER, TERMINAL_PAYMENT, } from '@yme/components/CheckoutForm/constants';
import { QueryParams } from '@yme/components/ShoppingCart/constants';
import { getIsCycleSchemeEnabled, getIsoCountryCode, getState } from '@yme/selectors/common';
import { getQueryParamByName } from '@yme/utils/common';
import { DEFAULT_DEALER_ID, DEFAULT_DESTINATION_CODE, DEFAULT_EXTERNAL_ID, DeliveryLocation, MAX_ADDRESS_LENGTH, } from './constants';
import { filterGYTRDealers, getIsGuestFlagValue, getMaxLengthStringValue, getShipmentLabel, getShippingAddressCountry, } from './helpers';
export const getCheckoutState = createSelector(getState, (state) => state.checkout);
export const getCheckoutLabels = createSelector(getCheckoutState, (state) => state.labels);
export const getAsGuestEmail = createSelector(getCheckoutState, (state) => state.asGuestEmail);
export const getSalutations = createSelector(getCheckoutState, (state) => state.salutations);
export const getCurrentStepNumber = createSelector(getCheckoutState, (state) => state.currentStepNumber);
export const getCurrentFormName = createSelector(getCheckoutState, (state) => state.currentFormName);
const getDealerData = createSelector(getCheckoutState, (state) => state.dealerData);
export const getDealerList = createSelector(getDealerData, (state) => state.dealerList);
export const getActivePreferDealerId = createSelector(getDealerData, (state) => state.activePreferDealerId);
export const getActivePreferDealer = createSelector([getDealerList, getActivePreferDealerId], (dealersList, dealerId) => find(dealersList, ({ vicinityId }) => vicinityId === dealerId));
export const getPersonalDetailsState = createSelector(getCheckoutState, (state) => state.personalDetailsData);
export const getPersonalData = createSelector(getPersonalDetailsState, (state) => state.data);
export const getPersonalDetailsEmail = createSelector(getPersonalDetailsState, (state) => state.data?.email.trim() || '');
export const getIsPersonalDetailsFieldErrors = createSelector(getPersonalDetailsState, (state) => state.isPersonalDetailsFieldErrors);
export const getIsShippingDetailsContinue = createSelector(getCheckoutState, (state) => state.isShippingDetailsContinue);
export const getPickUpAtDealerDisabled = createSelector(getCheckoutState, (state) => state.isPickUpAtDealerDisabled);
export const getShippingDetailState = createSelector(getCheckoutState, (state) => state.shippingDetailsData);
export const getShippingDetailData = createSelector(getShippingDetailState, (state) => state.data);
export const getIsShippingDetailsFieldErrors = createSelector(getShippingDetailState, (state) => state.isShippingDetailsFieldErrors);
export const getShippingDetailsPostalCode = createSelector(getShippingDetailState, (state) => state.data.postalCode);
export const getIsShippingDetailsErrors = createSelector(getCheckoutState, (state) => state.personalDetailsData.isPersonalDetailsFieldErrors ||
    state.shippingDetailsData.isShippingDetailsFieldErrors ||
    state.billingAddressData.isBillingAddressFieldErrors);
export const getLoqateKey = createSelector(getCheckoutState, (state) => state.loqateKey);
export const getGoogleKey = createSelector(getCheckoutState, (state) => state.googleKey);
export const getOrderReviewState = createSelector(getCheckoutState, (state) => state.orderReviewData);
export const getOrderStatus = createSelector(getOrderReviewState, (state) => state.status);
export const getOrderReviewModelLabels = createSelector(getCheckoutState, (state) => state.orderReviewModel.labels);
export const getOrderReviewTermsAndConditionsLink = createSelector(getCheckoutState, (state) => state.orderReviewModel.termsAndConditionsLink);
export const getIsBillingDealersSwapped = createSelector(getCheckoutState, (state) => state.isBillingDealersSwapped);
export const getIsPostalCodeInExclusionList = createSelector(getCheckoutState, (state) => state.isPostalCodeInExclusionList);
export const getPaymentProvider = createSelector(getCheckoutState, (state) => state.paymentProvider);
const getPayeverConfigModel = createSelector(getCheckoutState, (state) => state.payeverConfigModel);
export const getPayeverPaymentProvider = createSelector(getPayeverConfigModel, (state) => state.paymentProvider);
export const getIsPaymentProviderCycleScheme = createSelector(getPaymentProvider, (paymentProvider) => paymentProvider === CYCLESCHEME);
export const getIsPaymentProviderTerminal = createSelector(getPaymentProvider, (paymentProvider) => paymentProvider === TERMINAL_PAYMENT);
export const getIsPayeverPaymentProviderSantander = createSelector(getPayeverPaymentProvider, (provider) => provider === SANTANDER);
export const getIsPayeverPaymentProvider = createSelector(getPayeverPaymentProvider, (provider) => provider === PAYEVER);
export const getIsAdyenProvider = createSelector(getPaymentProvider, (provider) => provider === ADYEN);
export const isAmountInRange = createSelector([getState, getPayeverConfigModel], ({ cart }, { minPrice, maxPrice }) => {
    const amount = cart.data?.totalPrice?.amount ?? 0;
    return amount >= minPrice && amount <= maxPrice;
});
export const getAllShippingDetailsData = createSelector([getState, getActivePreferDealer], ({ common, cart, checkout, profileData, forms, eInvoice, nifNumber }, activePreferDealer) => {
    const isHomeDelivery = ShippingMethodsGroups.HOME.includes(cart.data?.shippingInfo?.name);
    const dealerStreet = activePreferDealer?.addressLines[0] || '';
    const { address: shippingAddress, state: shippingState, country: shippingCountry, } = checkout.shippingDetailsData.data;
    const { address: billingAddress, state: billingAddressState, country: billingAddressCountry, } = checkout.billingAddressData.data;
    const { sameAsShippingAddress } = checkout.billingAddressData;
    const { stateFieldEnabled, internalCountryFieldEnabled } = checkout.addressConfig;
    const shippingStreetName = isHomeDelivery
        ? getMaxLengthStringValue(shippingAddress)
        : getMaxLengthStringValue(dealerStreet);
    const shippingAdditionalStreetInfo = isHomeDelivery
        ? getMaxLengthStringValue(shippingAddress.substring(MAX_ADDRESS_LENGTH))
        : getMaxLengthStringValue(dealerStreet.substring(MAX_ADDRESS_LENGTH));
    const billingStreetName = sameAsShippingAddress
        ? shippingStreetName
        : getMaxLengthStringValue(billingAddress);
    const billingAdditionalStreetInfo = sameAsShippingAddress
        ? shippingAdditionalStreetInfo
        : getMaxLengthStringValue(billingAddress.substring(MAX_ADDRESS_LENGTH));
    const billingPostalCode = sameAsShippingAddress
        ? checkout.shippingDetailsData.data.postalCode
        : checkout.billingAddressData.data.postalCode;
    const billingCity = sameAsShippingAddress
        ? checkout.shippingDetailsData.data.city
        : checkout.billingAddressData.data.city;
    const billingState = sameAsShippingAddress ? shippingState : billingAddressState;
    const isPortugal = common.isoCountryCode === PORTUGAL_COUNTRY_CODE;
    const firstName = checkout.personalDetailsData.data?.firstName || '';
    const lastName = checkout.personalDetailsData.data?.lastName || '';
    const deliveryLocation = isHomeDelivery ? DeliveryLocation.HOME : DeliveryLocation.DEALER;
    const shippingAddressFirstName = isHomeDelivery
        ? firstName
        : getMaxLengthStringValue(activePreferDealer?.name ?? '');
    const shippingAddressLastName = isHomeDelivery ? lastName : '';
    const shippingAddressCity = isHomeDelivery
        ? checkout.shippingDetailsData.data.city
        : activePreferDealer?.city;
    const shippingAddressPostalCode = isHomeDelivery
        ? checkout.shippingDetailsData.data.postalCode
        : activePreferDealer?.zipCode;
    const shippingAddressCountry = getShippingAddressCountry(internalCountryFieldEnabled, shippingCountry, isHomeDelivery, common.isoCountryCode, activePreferDealer?.country);
    const shippingAddressEmail = isHomeDelivery
        ? checkout.personalDetailsData.data?.email
        : activePreferDealer?.email;
    const shippingAddressPhone = isHomeDelivery
        ? checkout.personalDetailsData.data?.phoneNumber
        : activePreferDealer?.telephoneNumbers[0];
    let eInvoiceData;
    if (isPortugal) {
        eInvoiceData = {
            fiscalCode: (nifNumber.isNIFNumberShowed && forms?.nifNumber?.fiscalCode.value) ||
                nifNumber.fiscalCode,
        };
    }
    else {
        eInvoiceData = eInvoice.isEInvoiceShowed
            ? {
                fiscalCode: forms.eInvoice.fiscalCode.value,
                destinationCode: DEFAULT_DESTINATION_CODE,
            }
            : null;
    }
    const billingCountry = sameAsShippingAddress ? shippingAddressCountry : billingAddressCountry;
    const isGuestCart = getIsGuestFlagValue(cart, profileData);
    const shipmentLabel = getShipmentLabel({
        firstName,
        lastName,
    });
    return {
        cartId: cart.data?.id || '',
        commercetoolsProject: common.commercetoolsProjectKey,
        shippingMethod: {
            id: cart.data?.shippingInfo?.shippingMethodId || '',
            name: cart.data?.shippingInfo?.name || '',
        },
        isGuestCart,
        dealer: {
            deliveryLocation,
            dealerId: activePreferDealer?.vicinityId || '',
            userId: profileData.data?.customAttributes?.myYamahaUserId || '',
            shipmentLabel,
            language: common.language.replace('-', '_') || '',
            eInvoice: !eInvoice.eInvoiceEnable || isPortugal ? '1' : eInvoice.isEInvoiceShowed ? '1' : '0',
            ...eInvoiceData,
        },
        shippingAddress: {
            firstName: shippingAddressFirstName,
            lastName: shippingAddressLastName,
            streetName: shippingStreetName,
            additionalStreetInfo: shippingAdditionalStreetInfo,
            streetNumber: '',
            city: shippingAddressCity || '',
            state: stateFieldEnabled ? shippingState : '',
            postalCode: shippingAddressPostalCode || '',
            country: shippingAddressCountry || '',
            email: shippingAddressEmail || '',
            phone: shippingAddressPhone || '',
            mobile: shippingAddressPhone || '',
            shippingOption: cart.data?.shippingInfo?.name || '',
        },
        billingAddress: {
            firstName,
            lastName,
            streetName: billingStreetName,
            additionalStreetInfo: billingAdditionalStreetInfo || '',
            streetNumber: '',
            city: billingCity || '',
            state: billingState,
            postalCode: billingPostalCode || '',
            country: billingCountry,
            email: checkout.personalDetailsData.data?.email || '',
            phone: checkout.personalDetailsData.data?.phoneNumber || '',
            mobile: checkout.personalDetailsData.data?.phoneNumber || '',
            externalId: DEFAULT_EXTERNAL_ID,
            shippingOption: cart.data?.shippingInfo?.name || '',
        },
        checkoutPagePath: `${common.languageRootPath}/checkout`,
        validatePostalCode: true,
        isRequestNotificationCart: checkout.isPostalCodeInExclusionList,
    };
});
export const getShippingDetailsEmailErrorMessage = createSelector(getCheckoutState, (state) => state.shippingDetailsEmailError);
export const getPayment = createSelector(getCheckoutState, (state) => state.payment);
export const getIsAccCreate = createSelector(getCheckoutState, (state) => state.isCreateAcc);
export const getBillingOption = createSelector(getCheckoutState, (state) => state.billingAddressData.billingOption);
export const getIsSameAsShippingAddress = createSelector(getCheckoutState, (state) => state.billingAddressData.sameAsShippingAddress);
export const getDealersToolsLabels = createSelector(getCheckoutState, (state) => state.dealersTools.labels);
export const getDefaultShippingDetailsData = createSelector([getState, getActivePreferDealer], ({ common, cart, checkout, profileData }, activePreferDealer) => {
    const isHomeDelivery = ShippingMethodsGroups.HOME.includes(cart.data?.shippingInfo?.name);
    const { address: billingAddress, postalCode: billingPostalCode, city: billingCity, } = checkout.billingAddressData.data;
    const billingStreetName = getMaxLengthStringValue(billingAddress);
    const billingAdditionalStreetInfo = getMaxLengthStringValue(billingAddress.substring(MAX_ADDRESS_LENGTH));
    const firstName = checkout.personalDetailsData.data?.firstName || '';
    const lastName = checkout.personalDetailsData.data?.lastName || '';
    const deliveryLocation = isHomeDelivery ? DeliveryLocation.HOME : DeliveryLocation.DEALER;
    const shippingAddressFirstName = isHomeDelivery
        ? firstName
        : getMaxLengthStringValue(activePreferDealer?.name ?? '');
    const shippingAddressLastName = isHomeDelivery ? lastName : '';
    const shippingAddressCountry = isHomeDelivery
        ? common.currentCountryRoot.toUpperCase()
        : activePreferDealer?.country;
    const shippingAddressEmail = isHomeDelivery
        ? checkout.personalDetailsData.data?.email
        : activePreferDealer?.email;
    const shippingAddressPhone = isHomeDelivery
        ? checkout.personalDetailsData.data?.phoneNumber
        : activePreferDealer?.telephoneNumbers[0];
    const userId = profileData.data?.customAttributes?.myYamahaUserId || '';
    const isGuestCart = getIsGuestFlagValue(cart, profileData);
    const shipmentLabel = getShipmentLabel({
        firstName,
        lastName,
    });
    return {
        cartId: cart.data?.id || '',
        commercetoolsProject: common.commercetoolsProjectKey,
        shippingMethod: {
            id: cart.data?.shippingInfo?.shippingMethodId || '',
            name: cart.data?.shippingInfo?.name || '',
        },
        isGuestCart,
        dealer: {
            deliveryLocation,
            dealerId: activePreferDealer?.vicinityId || DEFAULT_DEALER_ID,
            userId,
            shipmentLabel,
            language: common.language.replace('-', '_') || '',
            eInvoice: '1',
        },
        shippingAddress: {
            firstName: shippingAddressFirstName,
            lastName: shippingAddressLastName,
            streetName: '',
            additionalStreetInfo: '',
            streetNumber: '',
            city: '',
            state: '',
            postalCode: '',
            country: shippingAddressCountry || '',
            email: shippingAddressEmail || '',
            phone: shippingAddressPhone || '',
            mobile: shippingAddressPhone || '',
            shippingOption: cart.data?.shippingInfo?.name || '',
        },
        billingAddress: {
            firstName,
            lastName,
            streetName: billingStreetName,
            additionalStreetInfo: billingAdditionalStreetInfo || '',
            streetNumber: '',
            city: billingCity || '',
            state: '',
            postalCode: billingPostalCode || '',
            country: common.currentCountryRoot.toUpperCase() || '',
            email: checkout.personalDetailsData.data?.email || '',
            phone: checkout.personalDetailsData.data?.phoneNumber || '',
            mobile: checkout.personalDetailsData.data?.phoneNumber || '',
            externalId: DEFAULT_EXTERNAL_ID,
            shippingOption: cart.data?.shippingInfo?.name || '',
        },
        checkoutPagePath: `${common.languageRootPath}/checkout`,
    };
});
export const getPrivacyPolicyPageLink = createSelector(getCheckoutState, (state) => state.dealersTools.privacyPolicyPageLink ?? '');
export const getAddressConfig = createSelector(getCheckoutState, (state) => state.addressConfig);
export const getPostalCodeConfigModel = createSelector(getAddressConfig, (config) => config.postalCodeConfigModel);
export const getBillingCountries = createSelector(getCheckoutState, (state) => state.billingAddressData.billingCountries);
export const getBillingStates = createSelector(getCheckoutState, (state) => state.billingAddressData.billingStates ?? []);
export const getBillingAddressCountry = createSelector(getCheckoutState, (state) => state.billingAddressData.data.country);
export const getBillingAddressCountryFromCart = createSelector(getCart, (state) => state?.data?.billingAddress.country);
export const getFilteredDealerList = createSelector(getDealerList, getCartServiceCodes, getIsAllProductsApparel, getCartUnitServiceCodes, getIsOnlyGYTRProduct, getHasGYTRProduct, (allDealers, cartServiceCodes, isAllProductsApparel, cartUnitServiceCodes, isOnlyGYTRProduct, hasGYTRProduct) => {
    const preselectedDealerId = getQueryParamByName(window.location.search, QueryParams.PRESELECTED_DEALER_ID);
    const dealers = allDealers.filter((dealer) => dealer.pickUpCollect);
    const filteredDealersByAllCartServiceCode = dealers.filter((dealer) => cartServiceCodes.every((serviceCode) => dealer.serviceIds.find((serviceId) => serviceId.toLowerCase() === serviceCode.toLowerCase())));
    const filteredDealersByUnitCartServiceCode = dealers.filter((dealer) => cartUnitServiceCodes.every((serviceCode) => dealer.serviceIds.find((serviceId) => serviceId.toLowerCase() === serviceCode.toLowerCase())));
    let filteredDealers = [];
    if (isOnlyGYTRProduct) {
        filteredDealers = filterGYTRDealers(dealers);
    }
    else {
        let dealersByServices = [];
        if (isAllProductsApparel) {
            dealersByServices = dealers;
        }
        else if (filteredDealersByAllCartServiceCode.length && cartServiceCodes.length) {
            dealersByServices = filteredDealersByAllCartServiceCode;
        }
        else if (filteredDealersByUnitCartServiceCode.length && cartUnitServiceCodes.length) {
            dealersByServices = filteredDealersByUnitCartServiceCode;
        }
        else {
            dealersByServices = dealers;
        }
        filteredDealers = hasGYTRProduct ? filterGYTRDealers(dealersByServices) : dealersByServices;
    }
    if (preselectedDealerId &&
        !filteredDealers.some((dealer) => dealer.vicinityId === preselectedDealerId)) {
        const preselectedDealer = dealers.find((dealer) => dealer.vicinityId === preselectedDealerId);
        if (preselectedDealer) {
            filteredDealers.push(preselectedDealer);
        }
    }
    return filteredDealers;
});
const getIsAllUnitsAndTheSameAddress = createSelector([getIsLeasingEnabled, getIsSameAsShippingAddress, getIsCycleSchemeEnabled], (isLeasingEnabled, isSameAsShippingAddress, isCycleSchemeEnabled) => isCycleSchemeEnabled && isLeasingEnabled && isSameAsShippingAddress);
export const getIsAllUnitsAndIfOutsideUK = createSelector([getIsoCountryCode, getBillingAddressCountry, getIsAllUnitsAndTheSameAddress], (isoCountryCode, billingAddressCountry, isAllUnitsAndTheSameAddress) => !isAllUnitsAndTheSameAddress &&
    !isEmpty(billingAddressCountry) &&
    isoCountryCode !== billingAddressCountry);
export const getIsAllUnitsAndIfOutsideUKOnReview = createSelector([getIsoCountryCode, getBillingAddressCountryFromCart, getIsAllUnitsAndTheSameAddress], (isoCountryCode, billingAddressCountryFromCart, isAllUnitsAndTheSameAddress) => !isAllUnitsAndTheSameAddress &&
    !isEmpty(billingAddressCountryFromCart) &&
    isoCountryCode !== billingAddressCountryFromCart);
